/**
 * @features/property/services/propertyService.js
 * ----------------------------------------------
 * Centralised HTTP layer for everything property-related. Hooks and pages
 * MUST go through this module rather than calling `api.get("/properties/...")`
 * directly — that way:
 *
 *   • The endpoint path lives in ONE place. If `/api/properties/search`
 *     ever moves to `/api/v2/properties`, only this file changes.
 *   • Response shape is normalised at the seam (legacy `{results, total}`
 *     and new `{data, meta}` envelopes both come out as
 *     `{ items, total, page, pageSize, hasNext, hasPrev, meta }`).
 *   • Identical requests within a session are de-duped via the
 *     in-memory cache (see `./cache`).
 *
 * v2.71.26 — Initial extraction.
 */

import api from "../../../shared/services/api";
import { propertyCache, cacheKey } from "../cache";

/* ───────────────────────────────────────────────────────────────────────── */
/*  Search (paginated, sorted, filtered) — calls /api/properties/search       */
/* ───────────────────────────────────────────────────────────────────────── */

/**
 * Search properties with the v2.71.25 standardized envelope.
 *
 * @param {Object} opts
 * @param {Object} opts.filters    e.g. `{ main_area, bhk, listing_intent, min_price, max_price, tag, … }`
 * @param {number} opts.page       1-indexed (default 1).
 * @param {number} opts.pageSize   Items per page (default 24, max 100).
 * @param {string} opts.sort       `<field>:<asc|desc>` (default `created_at:desc`).
 * @param {AbortSignal} [opts.signal]  Optional axios cancel signal.
 * @returns {Promise<{
 *    items: Array, total: number, page: number, pageSize: number,
 *    hasNext: boolean, hasPrev: boolean, totalPages: number,
 *    sort: string, filters: Object|null, meta: Object
 * }>}
 */
export async function searchProperties({
  filters = {},
  page = 1,
  pageSize = 24,
  sort = "created_at:desc",
  signal,
} = {}) {
  const params = {
    page,
    page_size: pageSize,
    sort,
    ...filters,
  };
  // Strip empties so the URL stays clean and the cache key is stable.
  Object.keys(params).forEach((k) => {
    const v = params[k];
    if (v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0)) {
      delete params[k];
    }
  });

  const key = cacheKey("search", params);
  const cached = propertyCache.get(key);
  if (cached) return cached;

  const r = await api.get("/properties/search", { params, signal });
  const body = r.data || {};
  const meta = body.meta || {};
  const pagination = meta.pagination || {};
  const result = {
    items: body.data || [],
    total: pagination.total ?? 0,
    page: pagination.page ?? page,
    pageSize: pagination.page_size ?? pageSize,
    hasNext: !!pagination.has_next,
    hasPrev: !!pagination.has_prev,
    totalPages: pagination.total_pages ?? 0,
    sort: meta.sort || sort,
    filters: meta.filters || null,
    meta,
  };
  propertyCache.set(key, result);
  return result;
}

/* ───────────────────────────────────────────────────────────────────────── */
/*  Single property                                                          */
/* ───────────────────────────────────────────────────────────────────────── */

export async function getProperty(id, { signal } = {}) {
  if (!id) throw new Error("property id required");
  const r = await api.get(`/properties/${id}`, { signal });
  return r.data;
}

/* ───────────────────────────────────────────────────────────────────────── */
/*  Save / unsave (favorites)                                                */
/* ───────────────────────────────────────────────────────────────────────── */

/** Toggle the saved/favorite state. Returns the new `{ saved, saved_count }`. */
export async function toggleSave(id) {
  if (!id) throw new Error("property id required");
  const r = await api.post(`/properties/${id}/save`);
  // Invalidate the search cache for this session — saved-count on cards
  // may have shifted (legacy caches keep the stale count otherwise).
  propertyCache.invalidatePrefix("search");
  return r.data;
}

/** Get the current viewer's saved properties (single fetch, no pagination
 *  on backend today — covered by Phase 2.B follow-up). */
export async function getSavedProperties() {
  const r = await api.get("/properties/me/saved");
  return r.data;
}

/* ───────────────────────────────────────────────────────────────────────── */
/*  Behavioural signal — fires the lead-intelligence "viewed" event           */
/* ───────────────────────────────────────────────────────────────────────── */

export async function trackView(propertyId) {
  if (!propertyId) return null;
  try {
    const r = await api.post("/leads/track", {
      event: "property_view",
      property_id: propertyId,
    });
    return r.data;
  } catch {
    return null; // tracking is fire-and-forget; never break the UI
  }
}
